import { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { CHAT_STORE_STATUS } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useConversationById } from '@atlassian/conversation-assistant/controllers/chat-store';
import { useSmartCreationChatStoreApi } from '@atlassian/conversation-assistant/controllers/smart-creation-api';

import type { AiSuggestionsPlugin } from '../../aiSuggestionsPluginType';
import type { ThinkingStatus } from '../../pm-plugins/thinking-plugin';

/**
 * Builds the ADF prompt that triggers the suggest-edits skill. The text that
 * precedes the inline skill mention is passed in (already-localised) so the
 * prompt the AI service receives matches the user's locale.
 */
export const buildSuggestEditsPrompt = (precedingText: string): DocNode => ({
	type: 'doc',
	version: 1,
	content: [
		{
			type: 'paragraph',
			content: [
				{ type: 'text', text: precedingText },
				{
					type: 'inlineExtension',
					attrs: {
						extensionKey: 'skill:skillTag',
						extensionType: 'com.atlassian.rovo.skill',
						parameters: { slug: 'suggest-edits' },
					},
				},
			],
		},
	],
});

type UseSkillConversationParams = {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
	isRovoMounted: boolean;
	thinkingStatus: ThinkingStatus | undefined;
};

export type UseSkillConversationResult = {
	abortActiveConversation: () => void;
};

/**
 * Owns the conversation lifecycle for the suggest-edits skill triggered by the
 * Thinking surface:
 *  - When `thinkingStatus` flips to `'thinking'`, create an optimistic
 *    conversation and dispatch the suggest-edits skill prompt.
 *  - Watch the conversation status and transition the editor state:
 *      idle  → complete (or → stop, if Rovo Chat is open)
 *      error → stop
 *  - While Rovo is mounted and we're still thinking, surface the conversation
 *    in the Rovo chat panel via `setSelectedConversation`.
 *  - Expose an abort helper so the caller's stop handler can cancel the
 *    in-flight request without needing to know about the id.
 */
export function useSkill({
	api,
	thinkingStatus,
	isRovoMounted,
}: UseSkillConversationParams): UseSkillConversationResult {
	const { formatMessage } = useIntl();
	const { createConversationOptimistically, sendMessage, abortRequests, setSelectedConversation } =
		useSmartCreationChatStoreApi();

	const [conversationId, setConversationId] = useState<string | null>(null);

	const activeConversation = useConversationById({ conversationId: conversationId ?? '' });

	useEffect(() => {
		if (thinkingStatus === 'idle') {
			setConversationId(null);
			return;
		}

		if (thinkingStatus === 'thinking') {
			const nextConversationId = crypto.randomUUID();
			setConversationId(nextConversationId);

			const promptPrefix = formatMessage(aiSuggestionsMessages.suggestEditsPromptPrefix);

			const run = async () => {
				await createConversationOptimistically({ id: nextConversationId });
				sendMessage({
					prompt: buildSuggestEditsPrompt(promptPrefix),
					conversationId: nextConversationId,
					storeUserMessages: true,
					fastModeEnabled: true,
				});
			};

			try {
				run();
			} catch {
				api?.aiSuggestions?.actions.stopThinking();
			}
		}
	}, [api, thinkingStatus, createConversationOptimistically, sendMessage, formatMessage]);

	useEffect(() => {
		if (!activeConversation) {
			return;
		}

		let timeout: NodeJS.Timeout | undefined;
		switch (activeConversation.status) {
			case CHAT_STORE_STATUS.idle: {
				if (thinkingStatus === 'thinking') {
					if (isRovoMounted) {
						api?.aiSuggestions?.actions.stopThinking();
					} else {
						api?.aiSuggestions?.actions.refreshSuggestions(false);
						timeout = setTimeout(() => {
							api?.aiSuggestions?.actions.completeThinking();
						}, 3000);
					}
				}
				break;
			}
			case CHAT_STORE_STATUS.deactivatedAgentError: {
				api?.aiSuggestions?.actions.stopThinking();
				break;
			}
		}

		return () => {
			if (timeout) {
				clearTimeout(timeout);
			}
		};
	}, [activeConversation, api, isRovoMounted, thinkingStatus]);

	useEffect(() => {
		if (isRovoMounted && thinkingStatus !== 'idle' && conversationId) {
			setSelectedConversation({ conversationId });
		}
	}, [conversationId, isRovoMounted, setSelectedConversation, thinkingStatus]);

	const abortActiveConversation = useCallback(() => {
		if (conversationId) {
			abortRequests?.({ conversationId });
		}
	}, [abortRequests, conversationId]);

	return { abortActiveConversation };
}
